import { ethers } from 'ethers';
import { encodeFunctionData } from 'viem';

import useWrappedRPC from './useWrappedRPC';
import useToken from './useToken';
import { abiPaymentGateway } from '../config/abi.contract';
import {
  getGoldPurchaseSignature,
  validateGoldPurchaseTransaction,
  getPackPurchaseSignature,
  validatePackPurchaseTransaction,
  getDivingTicketPurchaseSignature,
  validateDivingTicketPurchaseTransaction,
  getLunarBaitPurchaseSignature,
  validateLunarBaitPurchaseTransaction,
  getCrownBaitPurchaseSignature,
  validateCrownBaitPurchaseTransaction,
} from '../services/transaction.service';
import environments from '../utils/environments';
import useUserStore from '../stores/user.store';

const {
  FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
  FISH_TOKEN_CONTRACT_ADDRESS,
  XFISH_TOKEN_CONTRACT_ADDRESS,
  USDC_ADDRESS,
} = environments;

export const tokens = {
  RON: ethers.constants.AddressZero,
  FISH: FISH_TOKEN_CONTRACT_ADDRESS,
  xFISH: XFISH_TOKEN_CONTRACT_ADDRESS,
  USDC: USDC_ADDRESS,
};

const tokenToCurrencyMap = Object.fromEntries(Object.entries(tokens).map(([key, value]) => [value, key]));

function usePurchase() {
  const { checkAndApproveToken } = useToken();
  const { sendTransactionWithGas } = useWrappedRPC();
  const user = useUserStore((state) => state.user);

  const purchaseGold = async (_amount) => {
    try {
      // default is RON
      // fbq('track', 'Purchase', { value: _amount, currency: 'RON', category: 'test' });
      const paymentTokenFE = ethers.constants.AddressZero;
      const res = await getGoldPurchaseSignature({ amount: _amount, paymentToken: paymentTokenFE });

      const {
        rawAmount: amount,
        purchaseType,
        paymentToken,
        packId,
        quantity,
        nonce,
        deadline,
        refType,
        referrer,
        signature,
      } = res.data;

      const parsedAmount = ethers.utils.parseEther(`${amount}`);
      const data = encodeFunctionData({
        abi: abiPaymentGateway,
        functionName: 'purchase',
        args: [
          purchaseType,
          paymentToken,
          parsedAmount,
          packId,
          quantity,
          nonce,
          deadline,
          refType,
          referrer,
          signature,
        ],
      });

      const transactionRequest = {
        to: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
        data,
        value: parsedAmount,
      };

      let tokenString;
      for (let key of Object.keys(tokens)) {
        if (tokens[key].toLowerCase() === paymentToken.toLowerCase()) tokenString = key;
      }
      const txUiConfig = {
        header: `Purchase Gold`,
        description: `Send ${amount} ${tokenString} to purchase gold`,
        buttonText: `Send`,
      };

      const receipt = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!receipt) throw new Error('Transaction failed on blockchain');

      const res1 = await validateGoldPurchaseTransaction({ transactionHash: receipt.hash });
      fbq('track', 'Purchase', { value: _amount, currency: 'RON', category: 'Purchase-Gold' });
      rdt('track', 'Purchase', { value: _amount, currency: 'RON', category: 'Purchase-Gold' });
      return {
        success: true,
        message: 'Purchase gold successful',
        data: res1.data,
      };
    } catch (error) {
      console.error('Purchase gold transaction failed:', error);
      return { success: false, message: error.message };
    }
  };

  const purchasePack = async (_amount, rawPackId, packName, token) => {
    try {
      const paymentTokenFE = tokens[token] || FISH_TOKEN_CONTRACT_ADDRESS;
      const res = await getPackPurchaseSignature({ packId: rawPackId, paymentToken: paymentTokenFE });
      const {
        rawAmount: amount,
        purchaseType,
        paymentToken,
        packId,
        quantity,
        nonce,
        deadline,
        refType,
        referrer,
        signature,
      } = res.data;

      // const parsedAmount = ethers.utils.parseEther(`${amount}`);

      const decimals = paymentTokenFE === tokens.USDC ? 6 : 18;
      const parsedAmount = ethers.utils.parseUnits(`${amount}`, decimals);

      const data = encodeFunctionData({
        abi: abiPaymentGateway,
        functionName: 'purchase',
        args: [
          purchaseType,
          paymentToken,
          parsedAmount,
          packId,
          quantity,
          nonce,
          deadline,
          refType,
          referrer,
          signature,
        ],
      });

      const isNativeToken = paymentToken === ethers.constants.AddressZero;

      const transactionRequest = {
        to: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
        data,
      };
      if (!isNativeToken)
        await checkAndApproveToken({
          tokenAddress: paymentToken,
          ownerAddress: user.walletAddress,
          spenderAddress: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
          amount,
          tokenDecimals: decimals,
        });
      else transactionRequest.value = parsedAmount;

      const txUiConfig = {
        header: `Purchase ${packName}`,
        description: `Send ${amount} ${token} to purchase ${packName}`,
        buttonText: `Send`,
      };

      const receipt = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!receipt) throw new Error('Transaction failed on blockchain');

      const res1 = await validatePackPurchaseTransaction({ transactionHash: receipt.hash, packId: rawPackId });
      const currency =
        paymentTokenFE === tokens.USDC
          ? 'USDC'
          : paymentTokenFE === tokens.FISH
            ? 'F  ISH'
            : paymentTokenFE === tokens.xFISH
              ? 'xFISH'
              : 'RON';
      fbq('track', 'Purchase', {
        value: _amount,
        currency: currency,
        category: 'Purchase-Package',
      });
      rdt('track', 'Purchase', {
        value: _amount,
        currency: currency,
        category: 'Purchase-Package',
      });
      return {
        success: true,
        message: `Purchase ${packName} successful`,
        data: res1.data,
      };
    } catch (error) {
      console.error('Purchase starter pack transaction failed:', error);
      return { success: false, message: error.message };
    }
  };

  const purchaseDivingTicket = async (quantity, token) => {
    try {
      const paymentTokenFE = tokens[token] || FISH_TOKEN_CONTRACT_ADDRESS;
      const res = await getDivingTicketPurchaseSignature({ quantity, paymentToken: paymentTokenFE });

      const {
        rawAmount: amount,
        purchaseType,
        paymentToken,
        packId,
        nonce,
        deadline,
        refType,
        referrer,
        signature,
      } = res.data;

      const parsedAmount = ethers.utils.parseEther(`${amount}`);
      const data = encodeFunctionData({
        abi: abiPaymentGateway,
        functionName: 'purchase',
        args: [
          purchaseType,
          paymentToken,
          parsedAmount,
          packId,
          quantity,
          nonce,
          deadline,
          refType,
          referrer,
          signature,
        ],
      });

      const isNativeToken = paymentToken === ethers.constants.AddressZero;

      const transactionRequest = {
        to: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
        data,
      };
      if (!isNativeToken)
        await checkAndApproveToken({
          tokenAddress: paymentToken,
          ownerAddress: user.walletAddress,
          spenderAddress: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
          amount,
        });
      else transactionRequest.value = parsedAmount;

      const txUiConfig = {
        header: `Purchase diving ticket`,
        description: `Send ${amount} ${token} to purchase a diving ticket`,
        buttonText: `Send`,
      };

      const receipt = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!receipt) throw new Error('Transaction failed on blockchain');

      const type = paymentTokenFE === 'RON' ? 'Premium' : 'Token';
      const res1 = await validateDivingTicketPurchaseTransaction({ transactionHash: receipt.hash, quantity, type });
      const currency =
        paymentTokenFE === tokens.USDC
          ? 'USDC'
          : paymentTokenFE === tokens.FISH
            ? 'F  ISH'
            : paymentTokenFE === tokens.xFISH
              ? 'xFISH'
              : 'RON';
      fbq('track', 'Purchase', {
        value: amount,
        currency: currency,
        category: 'Purchase-DivingTicket',
      });
      rdt('track', 'Purchase', {
        value: amount,
        currency: currency,
        category: 'Purchase-DivingTicket',
      });
      return {
        success: true,
        message: res1.data.result.message,
        data: res1.data,
      };
    } catch (error) {
      console.error('Purchase diving ticket:', error);
      return { success: false, message: error.message };
    }
  };

  const purchaseLunarBait = async (quantity) => {
    try {
      const res = await getLunarBaitPurchaseSignature({ quantity });

      const {
        rawAmount: amount,
        purchaseType,
        paymentToken,
        packId,
        nonce,
        deadline,
        refType,
        referrer,
        signature,
      } = res.data;

      const parsedAmount = ethers.utils.parseEther(`${amount}`);
      const data = encodeFunctionData({
        abi: abiPaymentGateway,
        functionName: 'purchase',
        args: [
          purchaseType,
          paymentToken,
          parsedAmount,
          packId,
          quantity,
          nonce,
          deadline,
          refType,
          referrer,
          signature,
        ],
      });

      const isNativeToken = paymentToken === ethers.constants.AddressZero;

      const transactionRequest = {
        to: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
        data,
      };
      if (!isNativeToken)
        await checkAndApproveToken({
          tokenAddress: paymentToken,
          ownerAddress: user.walletAddress,
          spenderAddress: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
          amount,
        });
      else transactionRequest.value = parsedAmount;

      const txUiConfig = {
        header: `Purchase lunar baits`,
        description: `Send ${amount} FISH to purchase ${quantity} lunar baits`,
        buttonText: `Send`,
      };

      const receipt = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!receipt) throw new Error('Transaction failed on blockchain');

      const res1 = await validateLunarBaitPurchaseTransaction({ transactionHash: receipt.hash });

      const currency =
        paymentToken === tokens.USDC
          ? 'USDC'
          : paymentToken === tokens.FISH
            ? 'FISH'
            : paymentToken === tokens.xFISH
              ? 'xFISH'
              : 'RON';
      fbq('track', 'Purchase', { value: amount, currency: currency, category: 'Purchase-LunarBait' });
      rdt('track', 'Purchase', {
        value: amount,
        currency: currency,
        category: 'Purchase-LunarBait',
      });
      return {
        success: true,
        message: `Purchase ${quantity} Lunar Bait${quantity > 1 ? 's' : ''} successful`,
        data: res1.data,
      };
    } catch (error) {
      console.error('Purchase lunar bait:', error);
      return { success: false, message: error.message };
    }
  };

  const purchaseCrownBait = async (quantity) => {
    try {
      const res = await getCrownBaitPurchaseSignature({ quantity });

      const {
        rawAmount: amount,
        purchaseType,
        paymentToken,
        packId,
        nonce,
        deadline,
        refType,
        referrer,
        signature,
      } = res.data;

      const parsedAmount = ethers.utils.parseEther(`${amount}`);
      const data = encodeFunctionData({
        abi: abiPaymentGateway,
        functionName: 'purchase',
        args: [
          purchaseType,
          paymentToken,
          parsedAmount,
          packId,
          quantity,
          nonce,
          deadline,
          refType,
          referrer,
          signature,
        ],
      });

      const isNativeToken = paymentToken === ethers.constants.AddressZero;

      const transactionRequest = {
        to: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
        data,
      };
      if (!isNativeToken)
        await checkAndApproveToken({
          tokenAddress: paymentToken,
          ownerAddress: user.walletAddress,
          spenderAddress: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
          amount,
        });
      else transactionRequest.value = parsedAmount;

      const txUiConfig = {
        header: `Purchase lunar baits`,
        description: `Send ${amount} FISH to purchase ${quantity} lunar baits`,
        buttonText: `Send`,
      };

      const txReceipt = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!txReceipt) throw new Error('Transaction failed on blockchain');

      const res1 = await validateCrownBaitPurchaseTransaction({ transactionHash: txReceipt.hash });

      const currency = tokenToCurrencyMap[paymentToken] ?? 'RON';
      fbq('track', 'Purchase', { value: amount, currency: currency, category: 'Purchase-CrownBait' });
      rdt('track', 'Purchase', {
        value: amount,
        currency: currency,
        category: 'Purchase-CrownBait',
      });
      return {
        success: true,
        message: `Purchase ${quantity} Crown Bait${quantity > 1 ? 's' : ''} successful`,
        data: res1.data,
      };
    } catch (error) {
      console.error('Purchase crown bait:', error);
      return { success: false, message: error.message };
    }
  };

  return { purchaseGold, purchasePack, purchaseDivingTicket, purchaseLunarBait, purchaseCrownBait };
}

export default usePurchase;
